import { Base2Presenter } from "core/presenter";
import { fn } from "util";
import validate from "util/validate";
import TaxesConstraint from "core/constraint/taxes.constraint";
import { ProductRepository } from "core/repository";
import _ from "lodash";

class BatchFiscalInformationsDialogPresenter extends Base2Presenter {
  initialState() {
    return {
      fiscal_informations: {},
      errors: {},
      ncmError: "",
      taxes: {
        use_default_configuration: true,
        ncm: "",
        csosn: "",
        cfop: "",
        cest: "",
        pis: "",
        cofins: "",
        origin: "",
        icms: {
          cst: "",
          cfop: "",
          modBC: "",
          pICMS: "",
          pFCP: "",
          modBCST: "",
          pMVAST: "",
          pRedBCST: "",
          pICMSST: "",
          pFCPST: "",
          pRedBC: "",
          motDesICMS: "",
          pST: "",
          pFCPSTRet: "",
          pRedBCEfet: "",
          pICMSEfet: ""
        },
        pis_normal: {
          cst: "",
          pPIS: "",
          vAliqProd: ""
        },
        cofins_normal: {
          cst: "",
          pCOFINS: "",
          vAliqProd: ""
        }
      }
    };
  }

  async onStart() {
    const currentUser = await this.mRuntime.get("currentUser");
    const { fiscal_informations } = currentUser.company;
    this.enqueue({ fiscal_informations });
  }

  enqueueField(field, value) {
    const { taxes } = this.viewModel;
    taxes[field] = value;
    this.enqueue({ taxes });
  }

  enqueueSubField(field, subField, value) {
    const { taxes } = this.viewModel;
    const pis_normal_fields = ["pPIS", "vAliqProd"];
    const cofins_normal_fields = ["pCOFINS", "vAliqProd"];
    const icms_fields = [
      "cfop",
      "modBC",
      "pICMS",
      "pFCP",
      "modBCST",
      "pMVAST",
      "pRedBCST",
      "pICMSST",
      "pFCPST",
      "pRedBC",
      "motDesICMS",
      "pST",
      "pFCPSTRet",
      "pRedBCEfet",
      "pICMSEfet"
    ];

    if (field === "pis_normal" && subField === "cst") {
      pis_normal_fields.forEach(field => (taxes["pis_normal"][field] = ""));
    } else if (field === "cofins_normal" && subField === "cst") {
      cofins_normal_fields.forEach(
        field => (taxes["cofins_normal"][field] = "")
      );
    } else if (field === "icms" && subField === "cst") {
      icms_fields.forEach(field => (taxes["icms"][field] = ""));
    }

    taxes[field][subField] = value;
    this.enqueue({ taxes });
  }

  onDefaultConfigurationChange(value) {
    const use_default_configuration = value
      ? value
      : !this.viewModel.taxes.use_default_configuration;
    this.enqueueField("use_default_configuration", use_default_configuration);
  }

  async validateFormTaxes(taxesToValidate) {
    const currentUser = await this.mRuntime.get("currentUser");
    const constraint = TaxesConstraint.taxes;

    const ncmToValidate = {
      state: currentUser.company.address.state || "SP",
      code: taxesToValidate.ncm
    };

    let validation =
      validate(taxesToValidate, constraint, { fullMessages: false }) || {};

    if (taxesToValidate.ncm && taxesToValidate.ncm.length) {
      const validatedNcm = await ProductRepository.validateTaxes(ncmToValidate);

      if (validatedNcm.status !== 403) {
        if (!validatedNcm.success) {
          // Validating size of field, if online validation fails
          const ncmConstraint = TaxesConstraint.ncm;
          const ncmValidation = validate(
            { ncm: taxesToValidate.ncm },
            ncmConstraint,
            { fullMessages: false }
          );
          validation = Object.assign({}, ncmValidation);
        }

        if (validatedNcm.data && !validatedNcm.data.found) {
          if (!validation) {
            validation = {};
          }
          validation.ncm = [
            "NCM inválido. Deixe em branco ou informe um valor válido."
          ];
        }
      }
    } else {
      delete validation.ncm;
    }

    if (!!validation && !_.isEmpty(validation)) {
      const errors = Object.entries(validation).map(v => {
        const field = v[0];
        const validationErrors = v[1];

        return {
          field,
          content: validationErrors.join(", ")
        };
      });

      this.enqueue({ errors: {} });

      errors.forEach(error => {
        const errors = this.viewModel.errors;
        errors[error.field] = error.content;
        this.enqueue({ errors });
      });

      return false;
    }

    this.enqueue({ errors: {} });
    return true;
  }

  parseFormTaxes(taxesToParse) {
    const parsedTaxes = taxesToParse;
    const taxes = [
      "cest",
      "cfop",
      "csosn",
      "ncm",
      "pis",
      "cofins",
      "origin",
      "gtin",
      "icms",
      "pis_normal",
      "cofins_normal"
    ];
    const crt3fields = {
      icms: [
        "cst",
        "cfop",
        "modBC",
        "pICMS",
        "pFCP",
        "modBCST",
        "pMVAST",
        "pRedBCST",
        "pICMSST",
        "pFCPST",
        "pRedBC",
        "motDesICMS",
        "pST",
        "pFCPSTRet",
        "pRedBCEfet",
        "pICMSEfet"
      ],
      pis_normal: ["cst", "pPIS", "vAliqProd"],
      cofins_normal: ["cst", "pCOFINS", "vAliqProd"]
    };

    taxes.forEach(tax => {
      if (fn.isBlank(parsedTaxes[tax]) || parsedTaxes[tax] === "none") {
        delete parsedTaxes[tax];
      } else {
        const crtTaxes = ["pis_normal", "cofins_normal", "icms"];
        if (crtTaxes.includes(tax)) {
          crt3fields[tax].forEach(field => {
            if (
              fn.isBlank(parsedTaxes[tax][field]) ||
              parsedTaxes[tax][field] === "none"
            ) {
              delete parsedTaxes[tax][field];
            }
          });
        }
      }
    });

    if (parsedTaxes.use_default_configuration) {
      const defaultTaxes = [
        "cfop",
        "csosn",
        "pis",
        "cofins",
        "origin",
        "icms",
        "pis_normal",
        "cofins_normal"
      ];
      defaultTaxes.forEach(tax => {
        delete parsedTaxes[tax];
      });
    }

    return parsedTaxes;
  }

  async onConfirmBatchFiscalInformations(taxes) {
    const isValid = await this.validateFormTaxes(taxes);
    if (isValid) {
      const parsedTaxes = this.parseFormTaxes(taxes);
      this.enqueue(
        { errors: {} },
        async () => await this.props.onSaveBatchFiscalInformations(parsedTaxes)
      );
    } else {
      this.sendNotification({
        type: "warning",
        content: "Existem campos inválidos. Verifique os dados preenchidos."
      });
    }
  }
}

export default BatchFiscalInformationsDialogPresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/product/list/data-table/batch-fiscal-informations-modal/presenter.js